import React from 'react';
import { PlayCircle } from 'lucide-react';
import { content } from '../data/content';

interface HeroProps {
  onDemoClick: () => void;
}

const Hero: React.FC<HeroProps> = ({ onDemoClick }) => {
  const { hero } = content;

  return (
    <header className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white px-4 md:px-6">
      {/* Spotlight Effect - Light Theme */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-100/60 via-white/0 to-transparent pointer-events-none"></div>
      
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-50/50 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3 animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-accent-50/50 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3 animate-pulse delay-1000"></div>
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-multiply"></div>
      
      <div className="container mx-auto relative z-10 pt-24 md:pt-20">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge - White Metallic Frosted Glass */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-br from-white/80 to-gray-100/50 backdrop-blur-xl border border-white/60 rounded-full px-4 md:px-6 py-2 mb-8 md:mb-12 animate-fade-in-up shadow-[0_8px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)] transition-all cursor-default ring-1 ring-white/80">
            <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]"></span>
            <span className="text-xs md:text-sm font-semibold tracking-wide text-gray-700 bg-clip-text text-transparent bg-gradient-to-r from-gray-800 to-gray-600">{hero.badge}</span>
          </div>
          
          {/* Title - Blue-Purple Gradient on both lines - Mobile Optimized */}
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold mb-8 md:mb-12 leading-[1.1] md:leading-[1.05] tracking-tight animate-fade-in-up delay-100 font-sans px-2">
            {hero.title.split('\n').map((line, i) => (
              <span key={i} className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 via-accent-600 to-primary-600 animate-gradient bg-[length:200%_auto]">
                {line}
              </span>
            ))}
          </h1>
          
          {/* CTA - Metallic Frosted Glass Button - Mobile Optimized */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 animate-fade-in-up delay-300 mt-8 md:mt-16">
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDemoClick();
              }}
              className="group inline-flex items-center justify-center px-8 md:px-12 py-4 md:py-5 rounded-full font-semibold text-primary-700 bg-gradient-to-br from-white/90 to-gray-100/80 backdrop-blur-xl border border-white/60 shadow-[0_8px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.08)] hover:bg-white transition-all duration-300 transform hover:scale-105 ring-1 ring-white/80 w-auto"
            >
              <PlayCircle className="mr-2 w-5 h-5 md:w-6 md:h-6 text-primary-500 group-hover:scale-110 transition-transform" />
              <span className="text-base md:text-lg">观看演示</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Hero;